import * as Sentry from '@sentry/vue';
import { auth, defaultRegion } from 'boot/firebase';
import { get } from 'lodash';
import { Platform } from 'quasar';
import { auth as useAuthStore } from 'src/store/auth';
import { region as useRegionStore } from 'src/store/region';

export default ({ app, router }) => {
  const region = localStorage.getItem('selected_region') || defaultRegion;

  // Sentry only provides services in the US and EU regions
  const allowedRegions = ['us', 'eu'];

  const offlineModeStatus = JSON.parse(localStorage.getItem('offlineModeStatus')) || false;
  const release = process.env.version;
  const environment = process.env.ENV_TYPE;

  const DSN_CONFIG = {
    us: environment === 'production' ? process.env.us.SENTRY_DSN : null,
    eu: process.env.eu.SENTRY_DSN,
  };

  const dsn = DSN_CONFIG[region];

  // Disable Sentry in offlineMode, in development env and for non US or EU region
  const enabled = !offlineModeStatus
    && ['staging', 'production'].includes(environment)
    && allowedRegions.includes(region)
    && !!dsn;

  if (!enabled) {
    return;
  }

  Sentry.init({
    app,
    dsn,
    environment,
    release,
    enabled,

    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        beforeErrorSampling: (event) => {
          return event.tags.enableReplay;
        },
      }),
    ],

    enableLogs: true,
    sendDefaultPii: true,
    // Trace sample rate; lowering for prod, 0.1 is common default:
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    maxBreadcrumbs: 50,

    beforeSend(event, hint) {
      const authStore = useAuthStore();
      const regionStore = useRegionStore();

      // User info
      const userId = auth.currentUser ? auth.currentUser.uid : 'Unknown';
      const userEmail = get(authStore.userDetails, 'email', 'Unknown');
      const userName = get(authStore.userDetails, 'name', 'Unknown');
      event.user = {
        id: userId,
        email: userEmail,
        username: userName,
      };

      // App/device info
      const storedRegion = get(regionStore, 'region', defaultRegion);
      event.contexts = {
        ...event.contexts,
        'Custom Meta': {
          region: storedRegion,
          mode: Platform.is.capacitor ? 'capacitor' : 'default',
          login_provider: localStorage.getItem('login_provider') || 'no value',
        },
        device: {
          brand: Platform.is.capacitor ? 'capacitor' : Platform.is.platform,
        },
      };

      if (event && hint && hint.originalException) {
        // Last resort handler for Firebase errors - refresh the app if Indexed DB crashed
        const error = hint.originalException.toString();
        if (error.indexOf('Refresh the page') >= 0) {
          // Refresh the page to restore IndexedDb to a working state.
          alert('A system error was encountered. We recommend restarting the app to prevent data-loss.');
          window.location.reload();
        }
        if (error.indexOf('INTERNAL ASSERTION FAILED: Unexpected state') >= 0) {
          // Refresh the page to restore IndexedDb to a working state.
          alert('Connection to server timed out. We recommend restarting the app to prevent data-loss.');
          window.location.reload();
        }
      }

      if (event && event.level !== 'error') {
        delete event.contexts.state;
        delete hint?.attachments;
      }

      return event;
    },
  });
};
